<template>
  <div class="pf-page">
    <q-card flat bordered class="pf-card pf-card--filters">
      <q-card-section class="pf-card__toolbar row items-center no-wrap">
        <div class="pf-card__toolbar-title">Параметры</div>
      </q-card-section>
      <q-separator class="pf-sep" />
      <q-card-section class="pf-filter-body">
        <div class="pf-filter-grid pf-filter-grid--row">
          <CustomSelect
            class="pf-field pf-field--grow"
            :options="residents"
            label="ЖК"
            option-value-key="resident_id"
            option-label-key="resident_name"
            :value="filters.resident_id"
            @change="(v) => onResident(v)"
          />
          <CustomSelect
            class="pf-field pf-field--grow"
            :options="projectTypes"
            label="Тип проекта"
            option-value-key="project_type_id"
            option-label-key="project_type_name"
            :value="filters.project_type_id"
            @change="(v) => onProjectType(v)"
          />
          <CustomSelect
            class="pf-field pf-field--grow pf-field--entrance"
            :options="entrances"
            label="Блок / подъезд"
            option-value-key="project_entrance_id"
            option-label-key="entrance_name"
            :loading="entrancesLoading"
            :value="filters.project_entrance_id"
            @change="(v, row) => onEntrance(v, row)"
          />
          <CustomSelect
            class="pf-field pf-field--grow pf-field--floor"
            :options="floorOptionsList"
            label="Этаж"
            option-value-key="floor_num"
            option-label-key="label"
            :clearable="true"
            :value="filters.floor"
            :disabled="!floorOptionsList.length"
            @change="onFloor"
          />
          <CustomSelect
            class="pf-field pf-field--grow"
            :options="placementTypes"
            label="Тип помещения"
            option-value-key="placement_type_id"
            option-label-key="placement_type_name"
            :clearable="true"
            :value="filters.placement_type_id"
            @change="(v) => onPlacementType(v)"
          />
          <q-select
            class="pf-field pf-field--grow pf-field--contractor"
            dense
            outlined
            clearable
            use-input
            hide-selected
            fill-input
            input-debounce="200"
            emit-value
            map-options
            hide-bottom-space
            label="Контрагент"
            option-value="contractor_id"
            option-label="contractor_name"
            :options="contractorSelectOptions"
            :model-value="contractorIdModel"
            :loading="contractorsLoading"
            @update:model-value="onContractorIdChange"
            @filter="filterContractors"
          >
            <template #no-option>
              <q-item>
                <q-item-section class="text-grey-6 text-caption">Нет совпадений</q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>
      </q-card-section>
    </q-card>

    <q-banner v-if="errorText" rounded class="pf-banner q-mt-md">
      <div class="pf-banner__text">{{ errorText }}</div>
    </q-banner>

    <q-linear-progress
      v-if="loading"
      indeterminate
      color="primary"
      track-color="grey-3"
      class="pf-progress q-mt-md"
      rounded
      size="4px"
    />

    <q-card v-if="flatRows.length" flat bordered class="pf-card pf-card--table q-mt-md">
      <q-card-section class="pf-table-toolbar q-py-sm q-px-md">
        <div class="row items-center no-wrap q-gutter-md flex-wrap">
          <div class="pf-table-toolbar__stats text-body2 text-grey-7">
            {{ displayRows.length }} строк · {{ flatRows.length }} работ в отчёте
          </div>
          <q-input
            v-model="treeSearch"
            class="pf-tree-search col-grow"
            dense
            outlined
            clearable
            placeholder="Поиск по названию"
            @update:model-value="scheduleApplyTreeSearch"
          >
            <template #prepend>
              <q-icon name="search" size="sm" class="text-grey-6" />
            </template>
          </q-input>
        </div>
      </q-card-section>

      <q-separator class="pf-sep" />
      <div class="pf-table-scroll">
        <table class="pf-table" cellspacing="0">
          <colgroup>
            <col class="pf-col-tree" />
            <col class="pf-col-contractor" />
            <col class="pf-col-dogovor" />
            <col v-for="n in 16" :key="'c' + n" class="pf-col-num" />
          </colgroup>
          <thead class="pf-thead">
            <tr class="pf-tr pf-tr--head pf-tr--group">
              <th rowspan="2" class="pf-th pf-th--sticky pf-th--tree">Конструктив</th>
              <th rowspan="2" class="pf-th pf-th--sticky-meta pf-th--meta">Контрагент</th>
              <th rowspan="2" class="pf-th pf-th--meta pf-th--dogovor" title="Номер договора по БК">Договор БК</th>
              <th colspan="3" class="pf-th pf-th--band pf-g--smeta pf-th--groupcell">Смета</th>
              <th colspan="3" class="pf-th pf-th--band pf-g--dog pf-th--groupcell">Договор</th>
              <th colspan="2" class="pf-th pf-th--band pf-g--dev pf-th--groupcell">Отклонение</th>
              <th colspan="2" class="pf-th pf-th--band pf-g--fact pf-th--groupcell">Факт</th>
              <th colspan="2" class="pf-th pf-th--band pf-g--ks2 pf-th--groupcell">КС2В</th>
              <th colspan="2" class="pf-th pf-th--band pf-g--avr pf-th--groupcell">АВР</th>
              <th colspan="2" class="pf-th pf-th--band pf-g--pay pf-th--groupcell">Платёж</th>
            </tr>
            <tr class="pf-tr pf-tr--head pf-tr--sub">
              <th class="pf-th pf-g--smeta pf-th--sub" title="Сумма без НДС">б/НДС</th>
              <th class="pf-th pf-g--smeta pf-th--sub" title="Сумма с НДС">с НДС</th>
              <th class="pf-th pf-g--smeta pf-th--sub" title="Ставка НДС, %">%</th>
              <th class="pf-th pf-g--dog pf-th--sub" title="Сумма без НДС">б/НДС</th>
              <th class="pf-th pf-g--dog pf-th--sub" title="Сумма с НДС">с НДС</th>
              <th class="pf-th pf-g--dog pf-th--sub" title="Ставка НДС, %">%</th>
              <th class="pf-th pf-g--dev pf-th--sub" title="Отклонение без НДС (смета − договор)">б/НДС</th>
              <th class="pf-th pf-g--dev pf-th--sub" title="Отклонение с НДС">с НДС</th>
              <th class="pf-th pf-g--fact pf-th--sub" title="Факт без НДС">б/НДС</th>
              <th class="pf-th pf-g--fact pf-th--sub" title="Отклонение договор − факт">Δ</th>
              <th class="pf-th pf-g--ks2 pf-th--sub" title="КС2В без НДС">б/НДС</th>
              <th class="pf-th pf-g--ks2 pf-th--sub" title="КС2В с НДС">с НДС</th>
              <th class="pf-th pf-g--avr pf-th--sub" title="АВР без НДС">б/НДС</th>
              <th class="pf-th pf-g--avr pf-th--sub" title="АВР с НДС">с НДС</th>
              <th class="pf-th pf-g--pay pf-th--sub" title="Платёж без НДС">б/НДС</th>
              <th class="pf-th pf-g--pay pf-th--sub" title="Платёж с НДС">с НДС</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in displayRows" :key="row._rowKey">
              <tr
                :class="[
                  'pf-tr',
                  row._isLeaf ? 'pf-tr--leaf' : 'pf-tr--node',
                  `pf-tr--depth-${row._depth}`,
                ]"
              >
                <td class="pf-td pf-td--tree pf-th--sticky" :style="{ paddingLeft: `${16 + row._depth * 18}px` }">
                  <span class="pf-tree-cell">
                    <button
                      v-if="!row._isLeaf"
                      type="button"
                      class="pf-expand"
                      :aria-expanded="expanded[row._path] === true"
                      @click="toggle(row._path)"
                    >
                      <span
                        class="pf-chevron"
                        :class="{ 'pf-chevron--open': expanded[row._path] === true }"
                        aria-hidden="true"
                      />
                    </button>
                    <span v-else class="pf-expand pf-expand--spacer" aria-hidden="true" />
                    <span :class="['pf-tree-label', `pf-tree-label--d${row._depth}`]">{{ row._label }}</span>
                  </span>
                </td>
                <td class="pf-td pf-td--meta pf-th--sticky-meta" :title="row._metaContractor">{{ row._metaContractor }}</td>
                <td class="pf-td pf-td--meta pf-td--dogovor" :title="row._metaDogovor">{{ row._metaDogovor }}</td>
                <td class="pf-td pf-num pf-g--smeta"><cell-amount :v="row.sum_bez_nds_smeta" /></td>
                <td class="pf-td pf-num pf-g--smeta"><cell-amount :v="row.sum_s_nds_smeta" /></td>
                <td class="pf-td pf-num pf-g--smeta">{{ fmtNds(row.nds_smeta) }}</td>
                <td class="pf-td pf-num pf-g--dog"><cell-amount :v="row.sum_bez_nds_dogovor" /></td>
                <td class="pf-td pf-num pf-g--dog"><cell-amount :v="row.sum_s_nds_dogovor" /></td>
                <td class="pf-td pf-num pf-g--dog">{{ fmtNds(row.nds_dogovor) }}</td>
                <td class="pf-td pf-num pf-g--dev"><cell-amount :v="row.otkl_bez_nds_smeta_dog" /></td>
                <td class="pf-td pf-num pf-g--dev"><cell-amount :v="row.otkl_s_nds_smeta_dog" /></td>
                <td class="pf-td pf-num pf-g--fact"><cell-amount :v="row.sum_bez_nds_fact" /></td>
                <td class="pf-td pf-num pf-g--fact"><cell-amount :v="row.otkl_bez_nds_dog_fact" /></td>
                <td class="pf-td pf-num pf-g--ks2"><cell-amount :v="row.sum_bez_nds_ks2v" /></td>
                <td class="pf-td pf-num pf-g--ks2"><cell-amount :v="row.sum_s_nds_ks2v" /></td>
                <td class="pf-td pf-num pf-g--avr"><cell-amount :v="row.sum_bez_nds_avr" /></td>
                <td class="pf-td pf-num pf-g--avr"><cell-amount :v="row.sum_s_nds_avr" /></td>
                <td class="pf-td pf-num pf-g--pay"><cell-amount :v="row.sum_bez_nds_payment" /></td>
                <td class="pf-td pf-num pf-g--pay"><cell-amount :v="row.sum_s_nds_payment" /></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </q-card>

    <q-card
      v-else-if="!loading && searched"
      flat
      bordered
      class="pf-empty q-mt-md"
    >
      <q-card-section class="column items-center q-py-xl q-px-md text-center">
        <div class="pf-empty__title">Нет данных</div>
        <div class="pf-empty__text">
          Смените фильтры или проверьте права на проект.
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
import { h } from 'vue';
import Amount from '@/components/common/Amount.vue';
import CustomSelect from '@/components/form/CustomSelect';
import {
  getPlanFactPaymentReport,
  getProjectTypes,
  getResidentList,
  getResidentialEntrances,
  getPlacementTypes,
  getContractorsList,
} from '@/views/ResidentialSettings/services';

const zeroSums = () => ({
  sum_bez_nds_smeta: null,
  sum_s_nds_smeta: null,
  nds_smeta: null,
  sum_bez_nds_dogovor: null,
  sum_s_nds_dogovor: null,
  nds_dogovor: null,
  otkl_bez_nds_smeta_dog: null,
  otkl_s_nds_smeta_dog: null,
  sum_bez_nds_fact: 0,
  otkl_bez_nds_dog_fact: null,
  sum_bez_nds_ks2v: null,
  sum_s_nds_ks2v: null,
  sum_bez_nds_avr: null,
  sum_s_nds_avr: null,
  sum_bez_nds_payment: 0,
  sum_s_nds_payment: 0,
});

function num(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Как в getResidentialEntrances: «Подъезд N» + при наличии «, Блок №…». */
function entranceRowLabel(row) {
  const e = row?.entrance;
  const b = row?.block_name;
  const hasE = e != null && e !== '';
  const hasB = b != null && String(b).trim() !== '';
  if (!hasE && !hasB) return '—';
  let s = hasE ? `Подъезд ${e}` : '';
  if (hasB) s += (s ? ', ' : '') + `Блок №${b}`;
  return s;
}

const CellAmount = {
  name: 'CellAmount',
  props: { v: [Number, String, Object] },
  render() {
    if (this.v === null || this.v === undefined || this.v === '') return h('span', { class: 'pf-dash' }, '—');
    return h(Amount, { value: this.v, round: '2' });
  },
};

export default {
  name: 'PlanFactPaymentReport',
  components: { CustomSelect, Amount, CellAmount },
  data() {
    return {
      residents: [],
      projectTypes: [],
      entrances: [],
      entrancesLoading: false,
      placementTypes: [],
      filters: {
        resident_id: '',
        project_type_id: '',
        project_entrance_id: '',
        floor: '',
        placement_type_id: '',
        contractor_id: '',
      },
      contractorsAll: [],
      contractorSelectOptions: [],
      contractorsLoading: false,
      /** Накопленные этажи из ответов отчёта (чтобы список не схлопывался при фильтре по этажу). */
      seenFloorNums: [],
      selectedProjectId: null,
      flatRows: [],
      treeRoots: [],
      expanded: {},
      loading: false,
      searched: false,
      errorText: '',
      treeSearch: '',
      _treeSearchTimer: null,
    };
  },
  computed: {
    floorOptionsList() {
      const set = new Set(this.seenFloorNums || []);
      const f = this.filters.floor;
      if (f !== '' && f != null) {
        const n = Number(f);
        if (!Number.isNaN(n)) set.add(n);
      }
      return Array.from(set)
        .filter((x) => !Number.isNaN(x))
        .sort((a, b) => a - b)
        .map((n) => ({ floor_num: n, label: String(n) }));
    },
    contractorIdModel() {
      const v = this.filters.contractor_id;
      if (v === '' || v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
    displayRows() {
      const rows = [];
      const walk = (nodes, parentOpen) => {
        if (!parentOpen) return;
        for (const n of nodes) {
          const open = n._isLeaf || this.expanded[n._path] === true;
          rows.push(n);
          if (!n._isLeaf && n.children?.length) walk(n.children, open);
        }
      };
      walk(this.treeRoots, true);
      return rows;
    },
  },
  async mounted() {
    this.residents = (await getResidentList()) || [];
    this.placementTypes = (await getPlacementTypes()) || [];
    this.expanded = {};
    this.loadContractors();
  },
  methods: {
    async loadContractors() {
      this.contractorsLoading = true;
      try {
        const list = (await getContractorsList()) || [];
        this.contractorsAll = list;
        this.contractorSelectOptions = list.slice(0, 120);
      } finally {
        this.contractorsLoading = false;
      }
    },
    filterContractors(val, update) {
      update(() => {
        const needle = String(val || '')
          .toLowerCase()
          .trim();
        const all = this.contractorsAll || [];
        if (!needle) {
          this.contractorSelectOptions = all.slice(0, 120);
          return;
        }
        this.contractorSelectOptions = all
          .filter((c) => {
            const name = String(c.contractor_name || '').toLowerCase();
            const legal = String(c.legal_name || '').toLowerCase();
            return name.includes(needle) || legal.includes(needle);
          })
          .slice(0, 200);
      });
    },
    onContractorIdChange(v) {
      this.filters.contractor_id = v == null || v === '' ? '' : v;
      this.refetchIfReady();
    },
    fmtNds(v) {
      if (v == null || v === '') return '—';
      return `${v}%`;
    },
    toggle(path) {
      const next = { ...this.expanded };
      if (next[path] === true) delete next[path];
      else next[path] = true;
      this.expanded = next;
    },
    scheduleApplyTreeSearch() {
      clearTimeout(this._treeSearchTimer);
      this._treeSearchTimer = setTimeout(() => this.applyTreeSearch(), 320);
    },
    /** Поиск: раскрыть всех предков строк с совпадением в названии; пустая строка — снова умолчание. */
    applyTreeSearch() {
      const q = (this.treeSearch || '').trim().toLowerCase();
      if (!q) {
        this.expanded = {};
        return;
      }
      const all = [];
      const walkCollect = (nodes) => {
        for (const n of nodes) {
          all.push(n);
          if (n.children?.length) walkCollect(n.children);
        }
      };
      walkCollect(this.treeRoots);
      const next = {};
      for (const n of all) {
        if (String(n._label || '').toLowerCase().includes(q)) {
          const parts = n._path.split('|');
          for (let i = 1; i < parts.length; i++) {
            next[parts.slice(0, i).join('|')] = true;
          }
        }
      }
      this.expanded = Object.keys(next).length ? next : {};
    },
    clearReport() {
      this.errorText = '';
      this.flatRows = [];
      this.seenFloorNums = [];
      this.treeRoots = [];
      this.searched = false;
      this.expanded = {};
      this.treeSearch = '';
    },
    mergeFloorsFromRows(rows) {
      if (!rows?.length) return;
      const s = new Set(this.seenFloorNums);
      for (const r of rows) {
        if (r.floor_num == null || r.floor_num === '') continue;
        const n = Number(r.floor_num);
        if (!Number.isNaN(n)) s.add(n);
      }
      this.seenFloorNums = Array.from(s).sort((a, b) => a - b);
    },
    onFloor(v) {
      this.filters.floor = v === null || v === undefined || v === '' ? '' : v;
      this.refetchIfReady();
    },
    refetchIfReady() {
      const rid = this.filters.resident_id;
      const pid = this.selectedProjectId;
      if (rid || pid) this.fetchReport();
    },
    async onResident(v) {
      this.filters.resident_id = v;
      this.filters.project_type_id = '';
      this.filters.project_entrance_id = '';
      this.filters.floor = '';
      this.seenFloorNums = [];
      this.selectedProjectId = null;
      this.projectTypes = [];
      this.entrances = [];
      if (!v) {
        this.clearReport();
        return;
      }
      this.projectTypes = (await getProjectTypes(v)) || [];
      await this.fetchReport();
    },
    async onProjectType(v) {
      this.filters.project_type_id = v;
      this.filters.project_entrance_id = '';
      this.filters.floor = '';
      this.seenFloorNums = [];
      this.selectedProjectId = null;
      this.entrances = [];
      if (this.filters.resident_id && v) {
        this.entrancesLoading = true;
        this.entrances = (await getResidentialEntrances({
          resident_id: this.filters.resident_id,
          project_type_id: v,
        })) || [];
        this.entrancesLoading = false;
      }
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onEntrance(v, row) {
      this.filters.project_entrance_id = v;
      this.filters.floor = '';
      this.seenFloorNums = [];
      this.selectedProjectId = v && row?.project_id != null ? row.project_id : null;
      if (this.filters.resident_id) await this.fetchReport();
      else if (this.selectedProjectId) await this.fetchReport();
    },
    onPlacementType(v) {
      this.filters.placement_type_id = v || '';
      this.refetchIfReady();
    },
    buildTree(flat) {
      const rootChildren = [];
      const findChild = (arr, key, factory) => {
        let n = arr.find((c) => c._path === key);
        if (!n) {
          n = factory();
          arr.push(n);
        }
        return n;
      };

      const projectIds = new Set(
        flat.map((x) => x.project_id).filter((id) => id != null && id !== ''),
      );
      const multiProject = projectIds.size > 1;
      const first = flat[0] || {};
      const rootMetaC = multiProject ? '—' : (first.contractor_display || '—');
      const rootMetaD = multiProject ? '—' : (first.dogovor_bk || '—');

      for (const r of flat) {
        const metaC = multiProject ? (r.contractor_display || '—') : rootMetaC;
        const metaD = multiProject ? (r.dogovor_bk || '—') : rootMetaD;
        const jk = `jk:${r.resident_id}`;
        const pt = `${jk}|pt:${r.project_type_id}`;
        const pe = `${pt}|pe:${r.project_entrance_id}`;
        const fl = `${pe}|fm:${r.floor_map_id}`;
        const wtSafe = String(r.work_type_name || '').replace(/\|/g, '/');
        const wt = `${fl}|sec:${r.section_id ?? 0}|${wtSafe}`;
        const leafKey = `${wt}|w:${r.work_set_id}:${r.work_set_check_group_id ?? 0}`;

        const n0 = findChild(rootChildren, jk, () => ({
          _path: jk,
          _rowKey: jk,
          _depth: 0,
          _isLeaf: false,
          _label: r.resident_name || 'ЖК',
          _metaContractor: rootMetaC,
          _metaDogovor: rootMetaD,
          children: [],
          ...zeroSums(),
        }));

        const n1 = findChild(n0.children, pt, () => ({
          _path: pt,
          _rowKey: pt,
          _depth: 1,
          _isLeaf: false,
          _label: r.project_type_name || '—',
          _metaContractor: metaC,
          _metaDogovor: metaD,
          children: [],
          ...zeroSums(),
        }));

        const n2 = findChild(n1.children, pe, () => ({
          _path: pe,
          _rowKey: pe,
          _depth: 2,
          _isLeaf: false,
          _label: entranceRowLabel(r),
          _metaContractor: metaC,
          _metaDogovor: metaD,
          children: [],
          ...zeroSums(),
        }));

        const n3 = findChild(n2.children, fl, () => ({
          _path: fl,
          _rowKey: fl,
          _depth: 3,
          _isLeaf: false,
          _label: `Этаж ${r.floor_num ?? '—'}`,
          _metaContractor: metaC,
          _metaDogovor: metaD,
          children: [],
          ...zeroSums(),
        }));

        const n4 = findChild(n3.children, wt, () => ({
          _path: wt,
          _rowKey: wt,
          _depth: 4,
          _isLeaf: false,
          _label: r.work_type_name || 'Прочее',
          _metaContractor: metaC,
          _metaDogovor: metaD,
          children: [],
          ...zeroSums(),
        }));

        const leaf = {
          _path: leafKey,
          _rowKey: leafKey,
          _depth: 5,
          _isLeaf: true,
          _label: r.work_name || '—',
          _metaContractor: metaC,
          _metaDogovor: metaD,
          /**
           * Для sumUp: в SQL смета/договор/KC2/АВР и отклонение смета−договор — одни суммы на
           * (project_id, floor_map_id, work_set_id, work_set_check_group_id); факт — по секции, не дедуплицируем.
           * Платёж — на (project_id, work_set_check_group_id).
           */
          project_id: r.project_id,
          floor_map_id: r.floor_map_id,
          work_set_id: r.work_set_id,
          work_set_check_group_id: r.work_set_check_group_id,
          children: [],
          sum_bez_nds_smeta: r.sum_bez_nds_smeta,
          sum_s_nds_smeta: r.sum_s_nds_smeta,
          nds_smeta: r.nds_smeta,
          sum_bez_nds_dogovor: r.sum_bez_nds_dogovor,
          sum_s_nds_dogovor: r.sum_s_nds_dogovor,
          nds_dogovor: r.nds_dogovor,
          otkl_bez_nds_smeta_dog: r.otkl_bez_nds_smeta_dog,
          otkl_s_nds_smeta_dog: r.otkl_s_nds_smeta_dog,
          sum_bez_nds_fact: r.sum_bez_nds_fact,
          otkl_bez_nds_dog_fact: r.otkl_bez_nds_dog_fact,
          sum_bez_nds_ks2v: r.sum_bez_nds_ks2v,
          sum_s_nds_ks2v: r.sum_s_nds_ks2v,
          sum_bez_nds_avr: r.sum_bez_nds_avr,
          sum_s_nds_avr: r.sum_s_nds_avr,
          sum_bez_nds_payment: r.sum_bez_nds_payment,
          sum_s_nds_payment: r.sum_s_nds_payment,
        };
        n4.children.push(leaf);
      }

      /** Денежные поля с SQL-агрегатом «на этаж×работу×группу» — дедуп по 4 ключам; платёж — по проект×группа. */
      const collectDedupedFromLeaves = (nodes, metMap, payBezMap, paySMap) => {
        for (const n of nodes || []) {
          if (n._isLeaf) {
            const pid = n.project_id;
            if (pid == null || pid === '') continue;
            const gid = n.work_set_check_group_id ?? 0;
            const fid = n.floor_map_id ?? '';
            const wid = n.work_set_id ?? '';
            const payKey = `${pid}|${gid}`;
            payBezMap.set(payKey, num(n.sum_bez_nds_payment));
            paySMap.set(payKey, num(n.sum_s_nds_payment));
            const metKey = `${pid}|${fid}|${wid}|${gid}`;
            metMap.set(metKey, {
              smetaBez: num(n.sum_bez_nds_smeta),
              smetaS: num(n.sum_s_nds_smeta),
              dogBez: num(n.sum_bez_nds_dogovor),
              dogS: num(n.sum_s_nds_dogovor),
              ks2Bez: num(n.sum_bez_nds_ks2v),
              ks2S: num(n.sum_s_nds_ks2v),
              avrBez: num(n.sum_bez_nds_avr),
              avrS: num(n.sum_s_nds_avr),
            });
          } else {
            collectDedupedFromLeaves(n.children, metMap, payBezMap, paySMap);
          }
        }
      };
      const sumDedupedRollups = (nodes) => {
        const metMap = new Map();
        const payBez = new Map();
        const payS = new Map();
        collectDedupedFromLeaves(nodes, metMap, payBez, payS);
        let smetaBez = 0;
        let smetaS = 0;
        let dogBez = 0;
        let dogS = 0;
        let otklBez = 0;
        let otklS = 0;
        let ks2Bez = 0;
        let ks2S = 0;
        let avrBez = 0;
        let avrS = 0;
        for (const v of metMap.values()) {
          smetaBez += v.smetaBez;
          smetaS += v.smetaS;
          dogBez += v.dogBez;
          dogS += v.dogS;
          otklBez += v.smetaBez - v.dogBez;
          otklS += v.smetaS - v.dogS;
          ks2Bez += v.ks2Bez;
          ks2S += v.ks2S;
          avrBez += v.avrBez;
          avrS += v.avrS;
        }
        let payBezSum = 0;
        let paySSum = 0;
        for (const v of payBez.values()) payBezSum += v;
        for (const v of payS.values()) paySSum += v;
        return {
          smetaBez,
          smetaS,
          dogBez,
          dogS,
          otklBez,
          otklS,
          ks2Bez,
          ks2S,
          avrBez,
          avrS,
          payBezSum,
          paySSum,
        };
      };

      /**
       * Средний % НДС по листьям внутри узла (для корня ЖК — «по всем» строкам отчёта).
       * Листья с пустым % в среднее не входят.
       */
      const setAverageNdsFromDescendantLeaves = (node) => {
        if (node._isLeaf) {
          const s =
            node.nds_smeta != null && node.nds_smeta !== "" && Number.isFinite(Number(node.nds_smeta))
              ? [Number(node.nds_smeta)]
              : [];
          const d =
            node.nds_dogovor != null && node.nds_dogovor !== "" && Number.isFinite(Number(node.nds_dogovor))
              ? [Number(node.nds_dogovor)]
              : [];
          return { smeta: s, dog: d };
        }
        let sm = [];
        let dg = [];
        for (const ch of node.children) {
          const x = setAverageNdsFromDescendantLeaves(ch);
          sm = sm.concat(x.smeta);
          dg = dg.concat(x.dog);
        }
        const mean2 = (arr) =>
          arr.length ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 100) / 100 : null;
        node.nds_smeta = mean2(sm);
        node.nds_dogovor = mean2(dg);
        return { smeta: sm, dog: dg };
      };

      const sumUp = (node) => {
        if (node._isLeaf) return;
        for (const ch of node.children) sumUp(ch);
        const ded = sumDedupedRollups(node.children);
        node.sum_bez_nds_smeta = ded.smetaBez;
        node.sum_s_nds_smeta = ded.smetaS;
        node.sum_bez_nds_dogovor = ded.dogBez;
        node.sum_s_nds_dogovor = ded.dogS;
        node.otkl_bez_nds_smeta_dog = ded.otklBez;
        node.otkl_s_nds_smeta_dog = ded.otklS;
        node.sum_bez_nds_fact = node.children.reduce((a, ch) => a + num(ch.sum_bez_nds_fact), 0);
        node.otkl_bez_nds_dog_fact = node.children.reduce((a, ch) => a + num(ch.otkl_bez_nds_dog_fact), 0);
        node.sum_bez_nds_ks2v = ded.ks2Bez;
        node.sum_s_nds_ks2v = ded.ks2S;
        node.sum_bez_nds_avr = ded.avrBez;
        node.sum_s_nds_avr = ded.avrS;
        node.sum_bez_nds_payment = ded.payBezSum;
        node.sum_s_nds_payment = ded.paySSum;
      };
      for (const n of rootChildren) sumUp(n);
      for (const n of rootChildren) setAverageNdsFromDescendantLeaves(n);
      this.treeRoots = rootChildren;
    },
    async fetchReport() {
      const rid = this.filters.resident_id;
      const pid = this.selectedProjectId;
      if (!rid && !pid) {
        this.clearReport();
        return;
      }
      this.errorText = '';
      this.loading = true;
      this.searched = true;
      try {
        const params = {
          project_entrance_id: this.filters.project_entrance_id || undefined,
          floor: this.filters.floor === '' || this.filters.floor == null ? undefined : this.filters.floor,
          project_type_id: this.filters.project_type_id || undefined,
          placement_type_id: this.filters.placement_type_id || undefined,
          contractor_id:
            this.filters.contractor_id === '' || this.filters.contractor_id == null
              ? undefined
              : Number(this.filters.contractor_id),
        };
        if (rid) params.resident_id = rid;
        else params.project_id = pid;

        const res = await getPlanFactPaymentReport(params);
        const data = res?.data || [];
        this.flatRows = data;
        this.mergeFloorsFromRows(data);
        this.treeSearch = '';
        this.buildTree(data);
        this.expanded = {};
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка загрузки';
        this.flatRows = [];
        this.treeRoots = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.pf-page {
  --pf-sticky-tree: clamp(200px, 22vw, 272px);
  --pf-sticky-contractor: 124px;
  --pf-col-dogovor: 92px;
  padding: 8px 12px 48px;
  max-width: 100%;
  min-height: calc(100vh - 56px);
  min-height: calc(100dvh - 56px);
  box-sizing: border-box;
}

.pf-card {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid $gray-light;
  background: #fff;
}

.pf-card__toolbar {
  padding: 12px 16px;
  gap: 12px;
}

.pf-card__toolbar-title {
  font-weight: 600;
  font-size: 14px;
  color: $title;
}

.pf-sep {
  opacity: 0.65;
}

.pf-filter-body {
  padding-top: 6px;
  padding-bottom: 2px;
}

.pf-filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 18px 22px;
  align-items: end;
}

.pf-filter-grid--row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 6px 8px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 2px;
  scrollbar-gutter: stable;

  .pf-field {
    flex: 1 0 0;
    min-width: 96px;
    max-width: 200px;
  }

  .pf-field--entrance {
    flex: 1.15 0 0;
    min-width: 120px;
    max-width: 240px;
  }

  .pf-field--floor {
    flex: 0.65 0 0;
    min-width: 72px;
    max-width: 100px;
  }

  .pf-field--contractor {
    flex: 1.1 0 0;
    min-width: 140px;
    max-width: 260px;
  }

  :deep(.custom-select__label) {
    font-size: 11px;
    line-height: 1.2;
    margin-bottom: 2px;
  }

  :deep(.q-field__label) {
    font-size: 11px;
    line-height: 1.2;
  }

  :deep(.q-field--dense .q-field__control),
  :deep(.custom-select .q-field__control) {
    min-height: 32px;
  }
}

.pf-field--wide {
  grid-column: span 2;
  @media (max-width: 720px) {
    grid-column: span 1;
  }
}

.pf-field--narrow {
  max-width: 200px;
}

.pf-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #78350f;
}

.pf-banner__text {
  font-size: 14px;
  line-height: 1.45;
}

.pf-progress {
  max-width: 100%;
}

.pf-card--table {
  padding-bottom: 0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.pf-table-toolbar {
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

.pf-table-toolbar__stats {
  flex: 0 0 auto;
  font-weight: 500;
}

.pf-tree-search {
  min-width: 200px;
  max-width: 520px;
}

.pf-table-scroll {
  overflow: auto;
  max-height: calc(100dvh - 120px);
  max-height: calc(100vh - 120px);
  min-height: min(58dvh, 520px);
  min-height: min(58vh, 520px);
  -webkit-overflow-scrolling: touch;
  border-radius: 0 0 12px 12px;
  box-shadow: inset 0 1px 0 rgba(15, 23, 42, 0.06);
  scrollbar-gutter: stable;
}

.pf-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}

.pf-col-tree {
  width: var(--pf-sticky-tree);
}

.pf-col-contractor {
  width: var(--pf-sticky-contractor);
}

.pf-col-dogovor {
  width: var(--pf-col-dogovor);
}

.pf-col-num {
  width: 74px;
}

.pf-thead {
  position: sticky;
  top: 0;
  z-index: 8;
  box-shadow: 0 2px 0 rgba(15, 23, 42, 0.08);
}

.pf-th,
.pf-td {
  border-bottom: 1px solid #e8ecf1;
  padding: 9px 8px;
  vertical-align: middle;
}

.pf-th {
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  font-size: 11px;
}

.pf-th-note {
  font-weight: 500;
  font-size: 10px;
  opacity: 0.88;
}

.pf-th--groupcell {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 700;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14) !important;
}

.pf-th--sub {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-top: 8px;
  padding-bottom: 8px;
}

.pf-tr--head .pf-th--tree,
.pf-tr--head .pf-th--meta,
.pf-tr--head .pf-th--dogovor {
  vertical-align: middle;
  line-height: 1.3;
  color: #f8fafc;
  background: #1e293b !important;
  border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.pf-th--sticky,
.pf-td--tree {
  position: sticky;
  left: 0;
  z-index: 5;
  text-align: left;
  width: var(--pf-sticky-tree);
  min-width: var(--pf-sticky-tree);
  max-width: var(--pf-sticky-tree);
  box-shadow: 4px 0 12px -6px rgba(15, 23, 42, 0.18);
}

.pf-th--sticky-meta,
.pf-td--meta.pf-th--sticky-meta {
  left: var(--pf-sticky-tree);
  z-index: 5;
  width: var(--pf-sticky-contractor);
  min-width: var(--pf-sticky-contractor);
  max-width: var(--pf-sticky-contractor);
  box-shadow: 4px 0 12px -6px rgba(15, 23, 42, 0.14);
}

.pf-thead .pf-th--sticky,
.pf-thead .pf-th--sticky-meta {
  z-index: 9;
}

.pf-tr--sub .pf-th--sticky,
.pf-tr--sub .pf-th--sticky-meta {
  z-index: 7;
}

.pf-td--meta,
.pf-td--dogovor {
  font-size: 11px;
  line-height: 1.35;
  word-break: break-word;
  hyphens: auto;
}

.pf-th--dogovor {
  max-width: var(--pf-col-dogovor);
}

.pf-g--smeta.pf-th--groupcell {
  background: #3b5bdb !important;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}

.pf-g--smeta.pf-th--sub {
  background: #eef2ff !important;
  color: #312e81 !important;
}

.pf-g--dog.pf-th--groupcell {
  background: #0d9488 !important;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}

.pf-g--dog.pf-th--sub {
  background: #ecfdf5 !important;
  color: #115e59 !important;
}

.pf-g--dev.pf-th--groupcell {
  background: #64748b !important;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}

.pf-g--dev.pf-th--sub {
  background: #f1f5f9 !important;
  color: #334155 !important;
}

.pf-g--fact.pf-th--groupcell {
  background: #ea580c !important;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}

.pf-g--fact.pf-th--sub {
  background: #fff7ed !important;
  color: #9a3412 !important;
}

.pf-g--ks2.pf-th--groupcell {
  background: #7c3aed !important;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}

.pf-g--ks2.pf-th--sub {
  background: #f5f3ff !important;
  color: #5b21b6 !important;
}

.pf-g--avr.pf-th--groupcell {
  background: #db2777 !important;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}

.pf-g--avr.pf-th--sub {
  background: #fdf2f8 !important;
  color: #9d174d !important;
}

.pf-g--pay.pf-th--groupcell {
  background: #475569 !important;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}

.pf-g--pay.pf-th--sub {
  background: #f8fafc !important;
  color: #0f172a !important;
}

.pf-g--smeta.pf-th--sub,
.pf-g--dog.pf-th--sub,
.pf-g--dev.pf-th--sub,
.pf-g--fact.pf-th--sub,
.pf-g--ks2.pf-th--sub,
.pf-g--avr.pf-th--sub,
.pf-g--pay.pf-th--sub {
  border-right: none;
}

.pf-g--smeta.pf-th--sub {
  box-shadow: 3px 0 0 #dbeafe inset;
}

.pf-g--dog.pf-th--sub {
  box-shadow: 3px 0 0 #a7f3d0 inset;
}

.pf-g--dev.pf-th--sub {
  box-shadow: 3px 0 0 #cbd5e1 inset;
}

.pf-g--fact.pf-th--sub {
  box-shadow: 3px 0 0 #fed7aa inset;
}

.pf-g--ks2.pf-th--sub {
  box-shadow: 3px 0 0 #ddd6fe inset;
}

.pf-g--avr.pf-th--sub {
  box-shadow: 3px 0 0 #fbcfe8 inset;
}

.pf-g--pay.pf-th--sub {
  box-shadow: 3px 0 0 #e2e8f0 inset;
}

.pf-td.pf-g--smeta {
  background: #f8faff;
}

.pf-td.pf-g--dog {
  background: #f6fdfb;
}

.pf-td.pf-g--dev {
  background: #fafbfc;
}

.pf-td.pf-g--fact {
  background: #fffbf5;
}

.pf-td.pf-g--ks2 {
  background: #faf8ff;
}

.pf-td.pf-g--avr {
  background: #fffafc;
}

.pf-td.pf-g--pay {
  background: #f9fafb;
}

.pf-tr--node .pf-td--tree {
  font-weight: 600;
  background: #f1f5f9 !important;
  color: $title;
  border-left: 3px solid #94a3b8;
}

.pf-tr--node .pf-th--sticky-meta,
.pf-tr--node .pf-td--meta.pf-th--sticky-meta {
  background: #eef2f6 !important;
}

.pf-tr--node .pf-td--dogovor {
  background: #eef2f6;
}

.pf-tr--node .pf-td--meta {
  font-weight: 500;
}

.pf-tr--leaf .pf-td--tree {
  font-weight: 500;
  background: #fff !important;
  border-left: 3px solid #e2e8f0;
}

.pf-tr--leaf .pf-th--sticky-meta,
.pf-tr--leaf .pf-td--meta.pf-th--sticky-meta {
  background: #fff !important;
}

.pf-tr--leaf .pf-td--dogovor {
  background: #fff;
}

.pf-tr--leaf:nth-child(even) .pf-td--tree,
.pf-tr--leaf:nth-child(even) .pf-td--meta.pf-th--sticky-meta,
.pf-tr--leaf:nth-child(even) .pf-td--dogovor {
  background: #fafbfc !important;
}

.pf-tr--leaf:nth-child(even) .pf-td.pf-g--smeta {
  background: #f1f5ff;
}

.pf-tr--leaf:nth-child(even) .pf-td.pf-g--dog {
  background: #ecfdf7;
}

.pf-tr--leaf:nth-child(even) .pf-td.pf-g--dev {
  background: #f4f6f8;
}

.pf-tr--leaf:nth-child(even) .pf-td.pf-g--fact {
  background: #fff5eb;
}

.pf-tr--leaf:nth-child(even) .pf-td.pf-g--ks2 {
  background: #f6f2ff;
}

.pf-tr--leaf:nth-child(even) .pf-td.pf-g--avr {
  background: #fff5f9;
}

.pf-tr--leaf:nth-child(even) .pf-td.pf-g--pay {
  background: #f3f4f6;
}

.pf-tr--leaf:hover .pf-td--tree,
.pf-tr--leaf:hover .pf-td--meta.pf-th--sticky-meta,
.pf-tr--leaf:hover .pf-td--dogovor {
  background: #e8f2ff !important;
}

.pf-tr--leaf:hover .pf-td.pf-num {
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.12);
}

.pf-tree-label--d0,
.pf-tree-label--d1,
.pf-tree-label--d2,
.pf-tree-label--d3,
.pf-tree-label--d4,
.pf-tree-label--d5 {
  border-left: 2px solid $gray-light;
  padding-left: 8px;
  margin-left: 2px;
}

.pf-tree-cell {
  display: flex;
  align-items: center;
  gap: 2px;
}

.pf-expand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0 4px 0 0;
  padding: 0;
  border: 1px solid $gray-light;
  border-radius: 4px;
  background: #fff;
  color: $gray-dark;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: $primary-light;
    background: #f8fafc;
  }

  &:focus-visible {
    outline: 2px solid $primary-light;
    outline-offset: 1px;
  }
}

.pf-expand--spacer {
  cursor: default;
  pointer-events: none;
  background: transparent !important;
}

.pf-chevron {
  display: block;
  width: 6px;
  height: 6px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(-45deg);
  transition: transform 0.18s ease;
}

.pf-chevron--open {
  transform: rotate(45deg);
  margin-top: -2px;
}

.pf-tree-label {
  vertical-align: middle;
  line-height: 1.4;
}

.pf-num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  font-size: 12px;
  padding: 7px 6px !important;
}

.pf-dash {
  color: $gray;
  opacity: 0.75;
}

.pf-empty {
  border-radius: 8px;
  background: #fafbfc;
  border: 1px solid $gray-light;
}

.pf-empty__title {
  font-size: 15px;
  font-weight: 600;
  color: $title;
  margin-bottom: 6px;
}

.pf-empty__text {
  font-size: 13px;
  line-height: 1.5;
  color: $gray;
  max-width: 360px;
}
</style>
